import { type CSSProperties, type ReactNode } from 'react';
import { ThemeModeSelector } from '@wavvest/shared-layout';
import { theme, type ThemeConfig } from 'antd';
import { LoginLayout } from '../LoginLayout';
import wavvestLogoIcon from './assets/wavvest-logo-icon.svg';
import wavvestLogoIconInk from './assets/wavvest-logo-icon-ink.svg';
import wavvestLogoText from './assets/wavvest-logo-text.svg';
import wavvestLogoTextInk from './assets/wavvest-logo-text-ink.svg';
import { getAuthBrandActionColors, getAuthBrandHeroAccentColor } from './authBrandColors';
import { type AuthColorScheme, type AuthThemeState, useAuthTheme } from './useAuthTheme';
import './auth-split-layout.scss';

const IMAGE_SOURCE_COMPARISON_BASE = 'https://wavvest.local';
const SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:/i;

const normalizeImageSrcForComparison = (src?: string): string => {
  const trimmed = src?.trim();
  if (!trimmed) {
    return '';
  }

  try {
    const hasScheme = SCHEME_PATTERN.test(trimmed);
    const url = hasScheme ? new URL(trimmed) : new URL(trimmed, IMAGE_SOURCE_COMPARISON_BASE);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      url.search = '';
      url.hash = '';
      if (!hasScheme && url.origin === IMAGE_SOURCE_COMPARISON_BASE) {
        return url.pathname;
      }
      return url.toString();
    }
  } catch {
    return trimmed;
  }

  return trimmed;
};

const getDistinctHeroImageSrc = (
  heroImageSrc?: string,
  logoSources: Array<string | undefined> = [],
): string | undefined => {
  const normalizedHero = normalizeImageSrcForComparison(heroImageSrc);
  if (!normalizedHero) {
    return undefined;
  }

  const duplicatesLogo = logoSources.some(
    (logoSource) => normalizeImageSrcForComparison(logoSource) === normalizedHero,
  );

  return duplicatesLogo ? undefined : heroImageSrc;
};

// The marketing site's type system (wavvest.com): Hanken Grotesk for
// interface copy, Fraunces for display. Ant components read their font from
// the theme token rather than inheriting it, so it is set there too.
const AUTH_SANS_STACK =
  "'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Primary action per scheme when the firm has no brand color. `accent` is the
// solid stand-in for places a gradient cannot go: focus rings, checkboxes and
// Ant's own colorPrimary.
const WAVVEST_ACTION: Record<AuthColorScheme, { accent: string; fill: string; ink: string }> = {
  // The marketing site's primary call to action: gold with dark type.
  dark: {
    accent: '#d4ae66',
    fill: 'linear-gradient(180deg, #e8c77e 0%, #c6a05a 100%)',
    ink: '#1a1206',
  },
  // Ink on white, matching the light auth surface.
  light: { accent: '#0b0d12', fill: '#0b0d12', ink: '#ffffff' },
};

const WAVVEST_HERO_ACCENT: Record<AuthColorScheme, string> = {
  dark: '#c6a05a',
  light: '#1b63b7',
};

const resolveActionColors = (scheme: AuthColorScheme, brandColor?: string) => {
  if (scheme === 'light' || !brandColor) {
    return WAVVEST_ACTION[scheme];
  }
  const { backgroundColor, foregroundColor } = getAuthBrandActionColors(brandColor, scheme);
  return { accent: backgroundColor, fill: backgroundColor, ink: foregroundColor };
};

const resolveHeroAccentColor = (
  scheme: AuthColorScheme,
  brandColor: string | undefined,
  hasHeroImage: boolean,
) => {
  const heroSurfaceScheme = hasHeroImage ? 'dark' : scheme;
  if (heroSurfaceScheme === 'light') {
    return WAVVEST_HERO_ACCENT.light;
  }
  if (!brandColor) {
    return WAVVEST_HERO_ACCENT[heroSurfaceScheme];
  }
  return getAuthBrandHeroAccentColor(brandColor, heroSurfaceScheme);
};

const authThemeComponents: ThemeConfig['components'] = {
  Button: {
    controlHeight: 44,
    fontSize: 15,
    borderRadius: 10,
    fontWeight: 600,
    primaryShadow: 'none',
    defaultShadow: 'none',
  },
  Input: {
    controlHeight: 44,
    fontSize: 15,
    borderRadius: 10,
  },
  Form: {
    itemMarginBottom: 16,
    labelFontSize: 13,
    verticalLabelPadding: '0 0 6px',
  },
};

const SignupProgress = ({
  activeStep = 1,
  totalSteps,
}: {
  activeStep?: number;
  totalSteps: number;
}) => (
  <div
    className="advisor-signup-progress"
    aria-label={`Signup step ${activeStep} of ${totalSteps}`}
  >
    {Array.from({ length: totalSteps }, (_, index) => index + 1).map((step, index) => (
      <div className="advisor-signup-progress-item" key={step}>
        <span
          className={`advisor-signup-progress-step${step === activeStep ? ' is-active' : ''}${
            step < activeStep ? ' is-complete' : ''
          }`}
        >
          {step}
        </span>
        {index < totalSteps - 1 && <span className="advisor-signup-progress-line" />}
      </div>
    ))}
  </div>
);

/**
 * How much room a firm's tagline needs. `introduction` is free text from the
 * firm's branding settings, so it can be a five-word motto or a paragraph. A
 * paragraph set at headline size would push everything else off the panel,
 * so long copy steps down and takes the product preview's space.
 */
export const heroTitleSize = (title: string): 'display' | 'medium' | 'long' => {
  const normalizedTitle = title.trim().replace(/\s+/g, ' ');
  const length = normalizedTitle.length;
  const wordCount = normalizedTitle ? normalizedTitle.split(' ').length : 0;

  if (length <= 48 && wordCount <= 7) {
    return 'display';
  }
  return length <= 96 && wordCount <= 14 ? 'medium' : 'long';
};

/** What the hero preview needs from the layout to match the active scheme. */
export type AuthHeroPreviewContext = {
  colorScheme: AuthColorScheme;
  /** The Wavvest mark in the ink that suits the active scheme. */
  logoMarkSrc: string;
};

const WavvestDisclaimer = () => (
  <>
    <p>Advisory services provided by Wavvest Wealth LLC, an SEC-registered investment adviser.</p>
    <p>
      By using this website, you agree to our{' '}
      <a href="https://wavvest-public-documents.s3.us-west-2.amazonaws.com/Wavvest/Wavvest+Terms+and+Conditions.pdf">
        Terms of Use
      </a>{' '}
      and <a href="https://www.wavvest.com/privacypolicy">Privacy Policy</a>. This site is protected
      by reCAPTCHA.
    </p>
  </>
);

export type AuthSplitLayoutProps = {
  children: ReactNode;
  /** Small label above the hero headline, e.g. "Advisor Portal". */
  kicker: string;
  /** Headline when the firm has no `introduction` of its own. */
  defaultHeroTitle: ReactNode;
  /** Accessible name for the hero panel. */
  heroAriaLabel: string;
  /** Product preview shown in the hero when there is no brand photo. */
  renderHeroPreview?: (context: AuthHeroPreviewContext) => ReactNode;
  logoSrc?: string;
  logoFallbackSrc?: string;
  logoAlt?: string;
  brandColor?: string;
  heroImageSrc?: string;
  /** The firm's own tagline; replaces `defaultHeroTitle` when present. */
  heroTitle?: string;
  /** Firm default theme, used when the visitor has not picked one. */
  defaultTheme?: AuthColorScheme | null;
  /**
   * Theme state from the page's own `useAuthTheme`. Pass it when the page also
   * depends on the scheme (e.g. picking a firm logo variant) so both read one
   * resolved value; otherwise the layout resolves the theme itself.
   */
  theme?: AuthThemeState;
  showProgress?: boolean;
  activeStep?: number;
  totalSteps?: number;
  /** Footer copy under the form. Defaults to the Wavvest Wealth disclaimer. */
  disclaimer?: ReactNode;
  /** Extra classes on the outer shell, appended to the shared ones. */
  className?: string;
};

export const AuthSplitLayout = ({
  children,
  kicker,
  defaultHeroTitle,
  heroAriaLabel,
  renderHeroPreview,
  logoSrc,
  logoFallbackSrc,
  logoAlt = 'Wavvest',
  brandColor,
  heroImageSrc,
  heroTitle,
  defaultTheme,
  theme: pageTheme,
  showProgress = false,
  activeStep = 1,
  totalSteps = 4,
  disclaimer,
  className,
}: AuthSplitLayoutProps) => {
  const ownTheme = useAuthTheme(defaultTheme);
  const { colorScheme, setThemePreference, themePreference } = pageTheme ?? ownTheme;
  const action = resolveActionColors(colorScheme, brandColor);
  const heroImageToDisplay = getDistinctHeroImageSrc(heroImageSrc, [logoSrc, logoFallbackSrc]);
  const heroAccent = resolveHeroAccentColor(colorScheme, brandColor, Boolean(heroImageToDisplay));
  const logoMarkSrc = colorScheme === 'light' ? wavvestLogoIconInk : wavvestLogoIcon;
  const firmTitle = heroTitle?.trim();
  const cssVariables = {
    '--auth-accent': action.accent,
    '--auth-action-fill': action.fill,
    '--auth-action-ink': action.ink,
    '--auth-hero-kicker': heroAccent,
  } as CSSProperties;
  const themeToken: ThemeConfig['token'] = {
    fontFamily: AUTH_SANS_STACK,
    colorPrimary: action.accent,
  };

  return (
    <LoginLayout
      className={`main advisor-signup-main advisor-signup-main--${colorScheme}${
        className ? ` ${className}` : ''
      }`}
      algorithm={colorScheme === 'light' ? theme.defaultAlgorithm : theme.darkAlgorithm}
      themeComponents={authThemeComponents}
      themeToken={themeToken}
    >
      <div className="advisor-signup-wrapper" style={cssVariables}>
        <section className="advisor-signup-form-panel">
          <ThemeModeSelector
            className="advisor-signup-theme-selector"
            onChange={setThemePreference}
            value={themePreference}
          />
          <div className="advisor-signup-top">
            {logoSrc ? (
              <div className="advisor-signup-logo advisor-signup-logo-image">
                <img
                  src={logoSrc}
                  alt={logoAlt}
                  onError={(event) => {
                    if (logoFallbackSrc && event.currentTarget.src !== logoFallbackSrc) {
                      event.currentTarget.src = logoFallbackSrc;
                    }
                  }}
                />
              </div>
            ) : (
              <div className="advisor-signup-logo advisor-signup-logo-default" aria-label="Wavvest">
                <span className="advisor-signup-logo-icon" aria-hidden="true">
                  <img src={logoMarkSrc} alt="" />
                </span>
                <img
                  className="advisor-signup-logo-wordmark"
                  src={colorScheme === 'light' ? wavvestLogoTextInk : wavvestLogoText}
                  alt=""
                  aria-hidden="true"
                />
              </div>
            )}
            {showProgress && <SignupProgress activeStep={activeStep} totalSteps={totalSteps} />}
          </div>
          <div className="advisor-signup-body">{children}</div>
          <div className="advisor-signup-disclaimer">{disclaimer ?? <WavvestDisclaimer />}</div>
        </section>
        <section
          className={`advisor-signup-hero advisor-signup-hero--${
            heroImageToDisplay ? 'brand-photo' : 'fallback-art'
          }`}
          data-title-size={firmTitle ? heroTitleSize(firmTitle) : 'display'}
          aria-label={heroAriaLabel}
        >
          {heroImageToDisplay && (
            <img
              className="advisor-brand-photo"
              src={heroImageToDisplay}
              alt=""
              aria-hidden="true"
            />
          )}
          <div className="advisor-hero-content">
            <p className="advisor-portal-kicker">{kicker}</p>
            <h2 className="advisor-hero-title">{firmTitle || defaultHeroTitle}</h2>
            {!heroImageToDisplay && renderHeroPreview?.({ colorScheme, logoMarkSrc })}
          </div>
        </section>
      </div>
    </LoginLayout>
  );
};
